/**
 * parseStringToDate
 * @description Parse string with dd/mm/yyyy format to date
 * @param date
 * @returns {Date}
 */
const parseStringToDate = (date) => {
  if (date === '') {
    return null;
  }
  const dateHub = date.split('/');
  return new Date(dateHub[2], dateHub[1] - 1, dateHub[0]);
};

/**
 * parseDateToString
 * @description Parse date to dd/mm/yyyy string format
 * @param date
 * @returns {string}
 */
const parseDateToString = (date) => {
  const dateDay = `0${date.getDate()}`.slice(-2);
  const dateMonth = `0${date.getMonth() + 1}`.slice(-2);
  const dateYear = date.getFullYear();
  return `${dateDay}/${dateMonth}/${dateYear}`;
};

/**
 * Returns the formatted first day (dd/MM/yyyy) of the month for a given date-like value.
 * @param {Date|string|number|null|undefined} dateInMonth
 * @returns {string}
 */
const getFirstOfMonthString = (dateInMonth) => {
  if (!dateInMonth) {
    return '';
  }
  const date = new Date(dateInMonth);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const firstDateOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
  return parseDateToString(firstDateOfMonth);
};

const getYesterdayDate = () => {
  const currentDate = new Date();
  const yesterdayDate = new Date();
  yesterdayDate.setDate(currentDate.getDate() - 1);
  return yesterdayDate;
};

const getTodayDate = () => new Date();

const getFormattedDate = (date) => date.toLocaleDateString('fr-FR');

/**
 * Returns a formatted date DD MMM YYYY (ex : 23 dec. 2023)
 * @param {String|Date|undefined} date
 * @returns {string}
 */
const getCompleteShorterReadableDate = (date = undefined) => {
  if (!date) {
    return '';
  }

  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  let dateObj;
  const match = typeof date === 'string' && date.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (match) {
    const [ day, month, year ] = match;
    dateObj = new Date(`${year}-${month}-${day}`);
  } else {
    dateObj = new Date(date);
  }

  if (Number.isNaN(dateObj.getTime())) {
    return '';
  }

  return dateObj.toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Returns a shorter formatted date DD MMM (ex : 23 déc)
 * @param {Date|String} date
 */
const getShorterReadableDate = (date = new Date()) => {
  const dateFormatted = new Date(date);
  const options = { month: 'short', day: 'numeric' };
  return dateFormatted.toLocaleDateString('fr-FR', options).replace('.', '');
};

/**
 * Returns a complete formatted date DD MMMM YYYY (ex : 23 décembre 2025)
 */
const getCompleteReadableDate = (dateStr) => {
  const dateFormatted = new Date(dateStr);
  const options = { day: 'numeric', month: 'long', year: 'numeric' };
  return dateFormatted.toLocaleDateString('fr-FR', options).replace('.', '');
};

/**
 * Returns a short formatted date DD MMMM (ex : 23 décembre)
 * @param {Date|String} date
 */
const getShortReadableDate = (date = new Date()) => {
  const dateFormatted = new Date(date);
  const options = { month: 'long', day: 'numeric' };
  return dateFormatted.toLocaleDateString('fr-FR', options);
};

const getReadableTime = (date = new Date()) => {
  const dateFormatted = new Date(date);
  const options = { hour: '2-digit', minute: '2-digit' };
  return dateFormatted.toLocaleTimeString('fr-FR', options);
};

const isDateInFuture = (date, reference = new Date()) => {
  const referenceDate = new Date(reference);
  const dateDate = new Date(date);
  return dateDate.getTime() > referenceDate.getTime();
};

const isDateInPast = (date, reference = new Date()) => {
  const referenceDate = new Date(reference);
  const dateDate = new Date(date);
  return dateDate.getTime() < referenceDate.getTime();
};

const isDateInThisMonth = (date) => {
  const dateObj = new Date(date);
  const now = new Date();

  return dateObj.getFullYear() === now.getFullYear() && dateObj.getMonth() === now.getMonth();
};

const isTodayBeforeDayOfMonth = (day) => {
  const targetDayOfMonth = Number(day);

  if (!Number.isInteger(targetDayOfMonth) || targetDayOfMonth < 1 || targetDayOfMonth > 31) {
    return false;
  }

  const today = new Date().getDate();
  return today < targetDayOfMonth;
};

const isSameDay = (date, reference = new Date()) => {
  const referenceDate = new Date(reference);
  const dateDate = new Date(date);
  return dateDate.toLocaleDateString() === referenceDate.toLocaleDateString();
};

const setToStartOfDay = (date = new Date()) => {
  const endOfToday = new Date(date);
  endOfToday.setHours(0);
  endOfToday.setMinutes(0);
  endOfToday.setSeconds(0);
  endOfToday.setMilliseconds(0);
  return endOfToday;
};

const setToEndOfDay = (date = new Date()) => {
  const endOfToday = new Date(date);
  endOfToday.setHours(23);
  endOfToday.setMinutes(59);
  endOfToday.setSeconds(59);
  endOfToday.setMilliseconds(999);
  return endOfToday;
};

/**
 * Returns a dd MMM YYYY formatted string (ex: 21 septembre 2025)
 */
const getHumanDate = (date) => new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(new Date(date));

const frMonthYear = new Intl.DateTimeFormat('fr-FR', { year: 'numeric', month: 'long' });
const formatMonthYear = (date) => {
  const s = frMonthYear.format(date);
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const getMonthDate = (dateStr) => {
  const [ day, month, year ] = dateStr.split('/').map(Number);
  return formatMonthYear(new Date(year, month - 1, day));
};

const getMonthDateFromIsoDate = (isoDateString) => formatMonthYear(new Date(isoDateString));

/**
 * Returns a dd/MM/YYYY formatted string
 */
const displayFrenchDate = (date) => new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
}).format(date);

/**
 * Returns formatted date and time (ex: 21 déc. 2025 - 21:30)
 * @param {Date|String} date
 * @param {String} separator
 * @param {Boolean} onlyDateIfMidnight
 * @returns {string}
 */
const getShortReadableDateTime = (date = new Date(), separator = ' - ') => {
  const dateFormatted = new Date(date);
  const dateOptions = { day: 'numeric', month: 'short', year: 'numeric' };
  const dateStr = dateFormatted.toLocaleDateString('fr-FR', dateOptions);

  const timeOptions = { hour: '2-digit', minute: '2-digit' };
  const timeStr = dateFormatted.toLocaleTimeString('fr-FR', timeOptions);

  return `${dateStr}${separator}${timeStr}`;
};

/**
 * Returns formatted date and time (ex: dimanche 21 déc.)
 * @param {Date|String} date
 * @returns {string}
 */
const getShortReadableDateWithWeekday = (date = new Date()) => {
  const dateFormatted = new Date(date);
  const dateOptions = { weekday: 'long', day: 'numeric', month: 'short' };

  return dateFormatted.toLocaleDateString('fr-FR', dateOptions);
};

/**
 * Returns formatted with day, date and time (ex: dimanche 21 déc. - 21:30)
 * ⚠ if datetime is 00:00, only date is returned
 * @param {Date|String} date
 * @param {String} separator
 * @returns {string}
 */
const getShortReadableDateTimeWithWeekday = (date = new Date(), separator = ' - ') => {
  const dateFormatted = new Date(date);
  const dateStr = getShortReadableDateWithWeekday(date);

  if (dateFormatted.getHours() === 0 && dateFormatted.getMinutes() === 0) {
    return dateStr;
  }

  const timeOptions = { hour: '2-digit', minute: '2-digit' };
  const timeStr = dateFormatted.toLocaleTimeString('fr-FR', timeOptions);

  return `${dateStr}${separator}${timeStr}`;
};

const endOfMonth = (date) => {
  const newDate = new Date(date);
  return new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0, 23, 59, 59);
};

const differenceInDays = (dateLeft, dateRight) => {
  const diffTime = Math.abs(dateLeft - dateRight);
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

const addDays = (date, days) => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

/**
 * Returns ISO 8601 date string with local timezone offset
 *
 * @param {Date} date
 * @returns {string} ISO date string with timezone (ex: "2026-04-14T15:30:45+02:00")
 * @example
 *
 * getIsoWithLocalTimezone(date); // "2026-04-14T15:30:45+02:00" (if timezone is UTC+2)
 */
const getIsoWithLocalTimezone = (date) => {
  const pad = (n) => String(n).padStart(2, '0');
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const absOffset = Math.abs(offset);

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    + `${sign}${pad(Math.floor(absOffset / 60))}:${pad(absOffset % 60)}`;
};

/**
 * @param {string} date
 * @returns {number}
 */
const getUnixTimeFromIsoDate = (date) => Math.floor(new Date(date).getTime() / 1000);

export {
  parseStringToDate,
  parseDateToString,
  getFirstOfMonthString,
  isDateInFuture,
  isDateInPast,
  isSameDay,
  getYesterdayDate,
  getTodayDate,
  getFormattedDate,
  getCompleteShorterReadableDate,
  getShortReadableDate,
  getShorterReadableDate,
  getShortReadableDateTime,
  getShortReadableDateWithWeekday,
  getShortReadableDateTimeWithWeekday,
  getReadableTime,
  setToStartOfDay,
  setToEndOfDay,
  getHumanDate,
  getMonthDate,
  isDateInThisMonth,
  isTodayBeforeDayOfMonth,
  displayFrenchDate,
  getMonthDateFromIsoDate,
  getCompleteReadableDate,
  endOfMonth,
  differenceInDays,
  addDays,
  getIsoWithLocalTimezone,
  getUnixTimeFromIsoDate,
};
